import { useCallback, useEffect, useState } from 'react';
import { useScopedLocalStorage } from '@/hooks/useScopedLocalStorage';
import { useRecordStatusContext } from '@/context/house/documents/RecordStatusContext';
import { kbBannerDismissedKey, kbFirstSetupDoneKey } from '@/utils/kbBannerStorage';
import { computeDocumentProgressValue, computeKbProgressValue } from '@/utils/kbProgressUtils';
import { useUpdateStatus } from '@/context/UpdateStatusContext';
import {
	shouldShowRecordsKnowledgeBaseBanner,
	type KnowledgeBaseReadinessState,
} from '@/hooks/house/documents/knowledgeBaseReadiness';

interface UseKnowledgeBaseBannerStateArgs {
	houseId: number;
	housePublicId: string;
	variant?: 'dashboard' | 'records';
}

const validateStoredFlag = (value: unknown): boolean => value === true;

interface TransientBannerDismissal {
	houseId: number;
	state: KnowledgeBaseReadinessState;
	documentIds: Set<number>;
}

export const shouldShowDashboardKnowledgeBaseBanner = ({
	readinessState,
	totalDocs,
	isFirstSetupDone,
	dismissed,
	hasTransientDismissal,
}: {
	readinessState: KnowledgeBaseReadinessState | null;
	totalDocs: number;
	isFirstSetupDone: boolean;
	dismissed: boolean;
	hasTransientDismissal: boolean;
}): boolean => readinessState !== null && totalDocs > 0 && isFirstSetupDone && !dismissed && !hasTransientDismissal;

export function useKnowledgeBaseBannerState(args: UseKnowledgeBaseBannerStateArgs) {
	const { houseId, housePublicId, variant = 'dashboard' } = args;
	const [isFirstSetupDone] = useScopedLocalStorage(kbFirstSetupDoneKey(housePublicId), false, validateStoredFlag);
	const [dismissed, setDismissed] = useScopedLocalStorage(
		kbBannerDismissedKey(housePublicId),
		false,
		validateStoredFlag,
	);
	const { aggregates } = useRecordStatusContext();
	const { knowledgeBaseReadinessSummary, documentReadinessSummary } = useUpdateStatus();
	const readinessSummary = variant === 'records' ? documentReadinessSummary : knowledgeBaseReadinessSummary;
	const readinessState = readinessSummary?.state ?? null;
	const [transientDismissal, setTransientDismissal] = useState<TransientBannerDismissal | null>(null);

	const dismiss = useCallback(() => {
		if (variant === 'records') {
			if (readinessState !== null) {
				setTransientDismissal({
					houseId,
					state: readinessState,
					documentIds: new Set(readinessSummary?.attentionDocumentIds ?? []),
				});
			}
			return;
		}
		setDismissed(true);
	}, [houseId, readinessState, readinessSummary?.attentionDocumentIds, setDismissed, variant]);

	useEffect(() => {
		if (readinessState === null || transientDismissal?.houseId !== houseId) setTransientDismissal(null);
	}, [houseId, readinessState, transientDismissal?.houseId]);

	useEffect(() => {
		// Readiness banners are intentionally one-shot per property: after the first fully completed
		// run, both dashboard and records variants remain retired via the persisted dismissal flag.
		if (aggregates.all_completed) setDismissed(true);
	}, [aggregates.all_completed, setDismissed]);

	const totalDocs = readinessSummary?.total ?? aggregates.total_docs;
	const hasTransientDismissal =
		readinessState !== null &&
		transientDismissal?.houseId === houseId &&
		transientDismissal.state === readinessState &&
		(readinessSummary?.attentionDocumentIds.every((id) => transientDismissal.documentIds.has(id)) ?? false);
	const recordsStateWarrantsBanner = shouldShowRecordsKnowledgeBaseBanner(readinessSummary);
	const isVisible =
		variant === 'records'
			? readinessState !== null &&
				totalDocs > 0 &&
				isFirstSetupDone &&
				!dismissed &&
				recordsStateWarrantsBanner &&
				!hasTransientDismissal
			: shouldShowDashboardKnowledgeBaseBanner({
					readinessState,
					totalDocs,
					isFirstSetupDone,
					dismissed,
					hasTransientDismissal,
				});
	const progressValue =
		variant === 'records'
			? computeDocumentProgressValue({ ...aggregates, total_docs: totalDocs })
			: computeKbProgressValue({ ...aggregates, total_docs: totalDocs });

	return {
		isVisible,
		progressValue,
		dismiss,
	} as const;
}
